import {validate, validateMany} from "../validate";
import {
    canEditSystemEntityById,
    contractSystemEntity,
    contractSystemEntityIsHidden,
    dateFromIsoToPlain,
    entityById,
} from "./systemEntityValueByKeyword";
import cleanPhoneNumber from "./cleanPhoneNumber";
import { cleanEmail } from "../funcs/cleanEmail";
import {
    clientEmail,
    clientLawAddress,
    clientLawName,
    clientOGRN,
    clientPassportIssuedAt,
    clientPassportIssuedBy,
    clientPassportIssuedByCode,
    clientPassportNumber,
    clientPassportPhoto,
    clientPassportRegistration,
    clientPassportRegistrationPhoto,
    clientPassportSelfiePhoto,
    clientPassportType, clientPhoneNumber, clientRole,
    clientSignature,
    clientTaxId,
    clientBankName,
    contactsEntities,
    customerPersonalEntities, entrepreneurAllEntities,
    entrepreneurBankEntities, isCustomerEntity, isEntrepreneurEntity, PassportType,
    SystemEntity,
    SystemEntityType,
    supportsPassportRegistrationFields,
    clientBirthDate,
} from "./SystemEntity";
import _ from "lodash";
import {capitalize} from "./notifyUser";
import {clientRoles} from "../pages/Auth/UserForms/ChooseAccountTypeFrom";
import {preparePhoneNumber} from "../funcs";
import {isEntityVisible} from "./entityVisibilityCondition";
import {BOOL_ENTITY_TYPE, inheritMissingSystemEntitiesFromClient, isBoolEntityEmpty} from "./workWithEntities";
import {
    hasOgrnField,
    normalizeJurisdictionCode,
    ogrnValidationRule,
    taxIdValidationRule,
} from "./jurisdictionIdentifiers";

export function extraFieldIsRequired(entity: any, isFirstSigner: boolean): boolean {
    if (!entity) {
        return false
    }

    if (isFirstSigner) {
        if (entity.adminCanFill) {
            if (entity.clientCanFill) {
                return false
            } else {
                return entity.isRequired ?? true
            }
        } else {
            return false
        }
    } else {
        return entity.clientCanFill && (entity.isRequired ?? true)
    }
}

function prepareIssuedAt(registerForm: any): string {
    if (registerForm.client_passport_issued_at) {
        return dateFromIsoToPlain(registerForm.client_passport_issued_at.trim())
    } else {
        return ''
    }
}

function prepareBirthDate(registerForm: any): string {
    if (registerForm.client_birth_date) {
        return dateFromIsoToPlain(registerForm.client_birth_date.trim())
    } else {
        return ''
    }
}

export function prepareDataInRegisterForm(registerForm: any): any {
    return {
        ...registerForm,
        client_last_name: capitalize(registerForm.client_last_name?.trim() ?? ''),
        client_first_name: capitalize(registerForm.client_first_name?.trim() ?? ''),
        client_middle_name: capitalize(registerForm.client_middle_name?.trim() ?? ''),
        client_birth_date: prepareBirthDate(registerForm),

        client_passport_type: registerForm.client_passport_type?.trim() ?? '',
        client_passport_number: (registerForm.client_passport_number ?? '').trim().replace(/\s+/g, '').replace(/(\d{4})(\d{6})/, '$1 $2'),
        client_passport_issued_by_code: (registerForm.client_passport_issued_by_code ?? '').trim().replace(/[\s\-]+/g, '').replace(/(\d{3})(\d{3})/, '$1-$2'),
        client_passport_issued_at: prepareIssuedAt(registerForm),
        client_passport_issued_by: registerForm.client_passport_issued_by?.trim() ?? '',
        client_passport_registration: registerForm.client_passport_registration?.trim() ?? '',

        client_signature: registerForm.client_signature?.trim() ?? '',
        
        client_passport_photo: registerForm.client_passport_photo?.trim() ?? '',
        client_passport_selfie_photo: registerForm.client_passport_selfie_photo?.trim() ?? '',
        client_passport_registration_photo: registerForm.client_passport_registration_photo?.trim() ?? '',

        client_law_name: registerForm.client_law_name?.trim() ?? '',
        client_law_address: registerForm.client_law_address?.trim() ?? '',
        client_tax_id: registerForm.client_tax_id?.trim() ?? '',
        client_ogrn: registerForm.client_ogrn?.trim() ?? '',

        client_bank_name: registerForm.client_bank_name?.trim() ?? '',
        client_bank_account: registerForm.client_bank_account?.trim() ?? '',
        client_corr_account: registerForm.client_corr_account?.trim() ?? '',
        client_bic: registerForm.client_bic?.trim() ?? '',

        client_email: cleanEmail(registerForm.client_email ?? ''),
        client_phone_number: cleanPhoneNumber(registerForm.client_phone_number ?? '')
    }
}


export type ValidationError = { fieldId: string; message: string }

export function validateSystemEntitiesInForm(
    system_entities: SystemEntity[],
    form: any,
    options?: { jurisdiction?: string | null, role?: string | null },
): ValidationError[] {
    const jurisdiction = normalizeJurisdictionCode(options?.jurisdiction)
    const role = options?.role ?? form.client_role

    let validationErrors: ValidationError[] = []
    for (const entity of system_entities) {
        let rule = ''
        if (entity.id == clientPassportNumber.id) {
            rule = form.client_passport_type === PassportType.russianPassport
                ? 'ruPassportNumber'
                : 'anotherPassportNumber'
        } else if (entity.id === clientTaxId.id) {
            rule = taxIdValidationRule(jurisdiction)
        } else if (entity.id === clientOGRN.id) {
            rule = ogrnValidationRule(jurisdiction) ?? ''
        } else {
            rule = entity.validationRule ?? ''
        }
        const value = _.get(form, entity.id, '')
        const result = validate(rule, value, entity.keyword)
        if (result) {
            validationErrors.push({ fieldId: entity.id, message: result })
        }
    }
    // Проверка для полей типа фото: значение должно быть заполнено и не короче 100 символов
    for (const entity of system_entities) {
        if (entity.type === SystemEntityType.Photo) {
            let value = _.get(form, entity.id, '')
            if (!value || value.length < 100) {
                validationErrors.push({
                    fieldId: entity.id,
                    message: !value
                        ? `Заполните поле "${entity.keyword}"`
                        : `Поле "${entity.keyword}" заполнено не правильно, заполните его заново.`
                })
            }
        }
    }
    return validationErrors
}

export function validateRegisterFormWhileCreatingContract(
    entitiesToValidate: SystemEntity[],
    form: any,
): string[] {
    let contractClientRole = entityById(entitiesToValidate, clientRole.id)
    let roleValue = contractClientRole?.value ?? ""
    
    // Если роль не выбрана, проверяем, что нет полей с editable==false для обеих ролей
    if (roleValue == "") {
        // Проверяем, есть ли поля customer с editable==false
        let hasCustomerNonEditableFields = entitiesToValidate.some((entity) => {
            return isCustomerEntity(entity.id) && !entity.hidden && !entity.editable
        })
        
        // Проверяем, есть ли поля entrepreneur с editable==false
        let hasEntrepreneurNonEditableFields = entitiesToValidate.some((entity) => {
            return isEntrepreneurEntity(entity.id) && !entity.hidden && !entity.editable
        })
        
        // Если есть поля с editable==false для обеих ролей или для одной из них, 
        // то нужно сначала выбрать роль
        if (hasCustomerNonEditableFields || hasEntrepreneurNonEditableFields) {
            // Валидируем все поля с editable==false (и customer, и entrepreneur)
            entitiesToValidate = entitiesToValidate.filter((entity) => {
                return (isCustomerEntity(entity.id) || isEntrepreneurEntity(entity.id)) 
                    && !entity.hidden && !entity.editable
            })
        } else {
            // Если нет полей с editable==false, не валидируем ничего
            return []
        }
    } else if (roleValue == clientRoles[0]) {
        // Роль entrepreneur - валидируем только поля entrepreneur с editable==false
        entitiesToValidate = entitiesToValidate.filter((entity) => {
            return isEntrepreneurEntity(entity.id) && !entity.hidden && !entity.editable
        })
    } else if (roleValue == clientRoles[1]) {
        // Роль customer - валидируем только поля customer с editable==false
        entitiesToValidate = entitiesToValidate.filter((entity) => {
            return isCustomerEntity(entity.id) && !entity.hidden && !entity.editable
        })
    } else {
        // Неизвестная роль - не валидируем
        return []
    }

    if (form.client_passport_type) {
        const entitiesToSkip = new Set<string>()
        if (form.client_passport_type !== PassportType.russianPassport) {
            entitiesToSkip.add(clientPassportIssuedByCode.id)
        }
        if (
            form.client_passport_type !== PassportType.russianPassport &&
            form.client_passport_type !== PassportType.belarusianPassport
        ) {
            entitiesToSkip.add(clientPassportIssuedBy.id)
        }
        if (!supportsPassportRegistrationFields(form.client_passport_type)) {
            entitiesToSkip.add(clientPassportRegistration.id)
            entitiesToSkip.add(clientPassportRegistrationPhoto.id)
        }
        if (entitiesToSkip.size > 0) {
            entitiesToValidate = entitiesToValidate.filter((entity) => {
                return !entitiesToSkip.has(entity.id)
            })
        }
    }

    return validateSystemEntitiesInForm(entitiesToValidate, form).map(e => e.message)
}

export function validateEntrepreneurRegisterFormWhileSigning(
    form: any,
    contract: any,
    is_first_signer: boolean,
    additionalSignerIndex?: number | null,
    jurisdiction?: string | null,
): ValidationError[] {
    const contractForExtra = contractForPassportSettings(contract, additionalSignerIndex)
    const effectiveJurisdiction = normalizeJurisdictionCode(jurisdiction)
    // contactsEntities (email/phone) — как на бэке в add_entrepreneur.validate_fields
    let entitiesToValidate = [clientLawName, clientLawAddress].concat(contactsEntities)

    if (form.client_tax_id.length > 0 || contract) {
        entitiesToValidate.push(clientTaxId)
    }

    const ogrnHidden = contract ? contractSystemEntityIsHidden(contractForExtra, clientOGRN.id) : false
    if ((form.client_ogrn.length > 0 || contract) && !ogrnHidden && hasOgrnField(effectiveJurisdiction)) {
        entitiesToValidate.push(clientOGRN)
    }

    if (contract) {
        entitiesToValidate = entitiesToValidate.concat(entrepreneurBankEntitiesToValidate(contractForExtra))
    }

    return validateSystemEntitiesInForm(entitiesToValidate, form, {
        jurisdiction: effectiveJurisdiction,
        role: form.client_role,
    })
        .concat(
            validateExtraEntities(
                extraEntitiesToSend(contract, is_first_signer, additionalSignerIndex),
                is_first_signer,
                contract?.entities ?? [],
            )
        )
}

export function validateCustomerRegisterFormWhileSigning(
    form: any,
    contract: any,
    is_first_signer: boolean,
    additionalSignerIndex?: number | null
): ValidationError[] {
    const contractForPassport = contractForPassportSettings(contract, additionalSignerIndex)
    let entitiesToValidate = customerPersonalEntities
        .concat(contactsEntities)
        .concat(passportEntitiesToValidate(contractForPassport, form))

    if (contract && !contractSystemEntityIsHidden(contractForPassport, clientBirthDate.id)) {
        entitiesToValidate = entitiesToValidate.concat([clientBirthDate])
    }

    return validateSystemEntitiesInForm(entitiesToValidate, form)
        .concat(
            validateExtraEntities(
                extraEntitiesToSend(contract, is_first_signer, additionalSignerIndex),
                is_first_signer,
                contract?.entities ?? [],
            )
        )
}

/**
 * Те же system_entities, что участвуют в validateCustomerRegisterFormWhileSigning / validateEntrepreneurRegisterFormWhileSigning
 * (шаг запроса кода), — для снимка формы ассистента без лишних полей.
 */
export function signingValidationSystemEntitiesForSnapshot(
    form: any,
    contract: any,
    additionalSignerIndex?: number | null
): SystemEntity[] {
    if (!contract || !form) {
        return []
    }
    const role = form.client_role
    const contractForPassport = contractForPassportSettings(contract, additionalSignerIndex)

    if (role === clientRoles[1]) {
        return customerPersonalEntities
            .concat(contactsEntities)
            .concat(passportEntitiesToValidate(contractForPassport, form))
            .concat(
                !contractSystemEntityIsHidden(contractForPassport, clientBirthDate.id)
                    ? [clientBirthDate]
                    : [],
            )
    }

    if (role === clientRoles[0]) {
        const contractForExtra = contractForPassportSettings(contract, additionalSignerIndex)
        let entitiesToValidate: SystemEntity[] = [clientLawName, clientLawAddress].concat(contactsEntities)

        if ((form.client_tax_id?.length ?? 0) > 0 || contract) {
            entitiesToValidate.push(clientTaxId)
        }

        const ogrnHidden = contract ? contractSystemEntityIsHidden(contractForExtra, clientOGRN.id) : false
        if (((form.client_ogrn?.length ?? 0) > 0 || contract) && !ogrnHidden && hasOgrnField(form.jurisdiction)) {
            entitiesToValidate.push(clientOGRN)
        }

        if (contract) {
            entitiesToValidate = entitiesToValidate.concat(entrepreneurBankEntitiesToValidate(contractForExtra))
        }
        return entitiesToValidate
    }

    return []
}

/** Заполненность поля формы в том же смысле, что и проверка фото в validateSystemEntitiesInForm (строка ≥ 100 символов). */
export function profileFieldFilledLikeSigningValidation(entity: SystemEntity, form: any): boolean {
    if (entity.type === SystemEntityType.Photo) {
        const value = _.get(form, entity.id, "")
        const s = typeof value === "string" ? value : ""
        return s.length >= 100
    }
    const value = _.get(form, entity.id, "")
    if (value == null) {
        return false
    }
    if (typeof value === "boolean") {
        return true
    }
    if (typeof value === "number") {
        return Number.isFinite(value)
    }
    if (typeof value === "string") {
        return value.trim().length > 0
    }
    if (Array.isArray(value)) {
        return value.length > 0
    }
    if (typeof value === "object") {
        return Object.keys(value as object).length > 0
    }
    return false
}

export function isExtraEntity(entity: any, is_first_signer: boolean) {
    if (is_first_signer) {
        return entity.adminCanFill
    } else {
        return entity.clientCanFill == true
    }
}

export function contractForPassportSettings(contract: any, additionalSignerIndex?: number | null): any {
    if (additionalSignerIndex != null && additionalSignerIndex >= 0 && contract?.additional_signers?.[additionalSignerIndex]) {
        return {
            ...contract,
            system_entities: inheritMissingSystemEntitiesFromClient(
                contract.additional_signers[additionalSignerIndex].system_entities,
                contract.system_entities,
            ),
        }
    }
    return contract
}

export function extraEntitiesToSend(contract: any, is_first_signer: boolean, additionalSignerIndex?: number | null) {
    if (additionalSignerIndex != null && additionalSignerIndex >= 0 && contract?.additional_signers?.[additionalSignerIndex]) {
        const systemEntities = contract.additional_signers[additionalSignerIndex].system_entities ?? []
        return systemEntities.filter((entity: any) => !entity.hidden && (entity.editable !== false || entity.clientCanFill))
    }
    return contract?.entities?.filter((entity: any) => {
        return isExtraEntity(entity, is_first_signer) || entity.type == SystemEntityType.Formula.toString()
    }) ?? []
}

export function validateExtraEntities(
    entities: any,
    isFirstSigner: boolean,
    allEntities?: any[],
): ValidationError[] {
    const sourceEntities = allEntities ?? entities
    let validationErrors: ValidationError[] = []
    for (const entity of entities) {
        if (!isEntityVisible(entity, sourceEntities)) {
            continue
        }
        const isEmpty = entity.type === BOOL_ENTITY_TYPE
            ? isBoolEntityEmpty(entity)
            : !entity.value
        if (extraFieldIsRequired(entity, isFirstSigner) && isEmpty) {
            validationErrors.push({
                fieldId: entity.id || entity.keyword,
                message: `Поле "${entity.keyword}" заполнено не правильно.`
            })
        }
    }
    return validationErrors
}

export type PassportSettingsAdapter = {
    enabled: boolean
    number: boolean
    issued_by: boolean
    issued_by_code: boolean
    issued_at: boolean
    signature: boolean
    registration: boolean
    photo: boolean
    selfie_photo: boolean
    registration_photo: boolean
    photos_only_from_camera: boolean
}

export function makePassportSettingsAdapter(contract: any): PassportSettingsAdapter {
    // Форма следует галочкам (hidden). Поиск {{Паспорт клиента}} в HTML — только демо
    // (applyDemoContractPrefill). Не возвращать сюда htmlRequestsClientPassport:
    // живые шаблоны без метки в тексте теряли поля паспорта при включённой галочке.
    function isShown(entity: SystemEntity): boolean {
        let contractEntity = contractSystemEntity(contract, entity.id) || contractSystemEntity(contract, entity.keyword)
        if (!contractEntity) {
            return false
        }
        return !(contractEntity.hidden ?? entity.id != clientPassportNumber.id)
    }

    return {
        enabled: isShown(clientPassportNumber),
        number: isShown(clientPassportNumber),
        issued_by_code: isShown(clientPassportIssuedByCode),
        issued_at: isShown(clientPassportIssuedAt),
        signature: isShown(clientSignature),
        issued_by: isShown(clientPassportIssuedBy),
        registration: isShown(clientPassportRegistration),

        photo: isShown(clientPassportPhoto),
        selfie_photo: isShown(clientPassportSelfiePhoto),
        registration_photo: isShown(clientPassportRegistrationPhoto),

        photos_only_from_camera: clientPassportPhoto.onlyFromCamera == true || clientPassportSelfiePhoto.onlyFromCamera == true || clientPassportRegistrationPhoto.onlyFromCamera == true   
    }
}

export function passportEntitiesToValidate(contract: any, form: any): SystemEntity[] {
    if (!contract) {
        return []
    }

    let passportSettings = makePassportSettingsAdapter(contract)
    if (!passportSettings.enabled) {
        return []
    }

    let entitiesToValidate = [clientPassportNumber]

    if (passportSettings.issued_at) {
        entitiesToValidate.push(clientPassportIssuedAt)
    }

    if (passportSettings.issued_by_code && form.client_passport_type === PassportType.russianPassport) {
        entitiesToValidate.push(clientPassportIssuedByCode)
    }

    const isRussianOrBelarusian = form.client_passport_type === PassportType.russianPassport || form.client_passport_type === PassportType.belarusianPassport
    if (passportSettings.issued_by && isRussianOrBelarusian) {
        entitiesToValidate.push(clientPassportIssuedBy)
    }

    if (passportSettings.registration && supportsPassportRegistrationFields(form.client_passport_type)) {
        entitiesToValidate.push(clientPassportRegistration)
    }

    if (passportSettings.signature) {
        entitiesToValidate.push(clientSignature)
    }

    if (passportSettings.photo) {
        entitiesToValidate.push(clientPassportPhoto)
    }
    if (passportSettings.selfie_photo) {
        entitiesToValidate.push(clientPassportSelfiePhoto)
    }
    if (passportSettings.registration_photo && supportsPassportRegistrationFields(form.client_passport_type)) {
        entitiesToValidate.push(clientPassportRegistrationPhoto)
    }

    return entitiesToValidate
}

export function entrepreneurBankEntitiesToValidate(contract: any): SystemEntity[] {
    if (!contract) {
        return []
    }

    // Проверяем, скрыты ли банковские реквизиты
    let bankNameEntity = contractSystemEntity(contract, clientBankName.id) ?? clientBankName
    if (bankNameEntity.hidden) {
        return []
    }

    // Если банковские реквизиты не скрыты, возвращаем все банковские сущности
    return entrepreneurBankEntities
}

export function validateContractCreatedForAndUser(contract: any, form: any, user: any): string {
    return validateContractCreatedForAndUserEmail(contract, form, user) ||
        validateContractCreatedForAndUserPhoneNumber(contract, form, user) ||
        validateContractCreatedForAndClientRole(contract, form, user)
}

export function validateContractCreatedForAndUserEmail(contract: any, form: any, user: any): string {
    if (!user) {
        return ''
    }
    let emailDisabled = !canEditSystemEntityById(contract, clientEmail.id)
    let formEmail = cleanEmail(form.client_email ?? "")
    let userEmail = cleanEmail(user.email ?? "")
    if (emailDisabled && formEmail != userEmail) {
        return `Договор нельзя подписать так как он выставлен на почту ${formEmail}, а почта вашего аккаунта ${userEmail}`
    } else {
        return ''
    }
}

export function validateContractCreatedForAndUserPhoneNumber(contract: any, form: any, user: any): string {
    if (!user) {
        return ''
    }
    let phoneNumberDisabled = !canEditSystemEntityById(contract, clientPhoneNumber.id)
    let client_phone_number = preparePhoneNumber(form.client_phone_number)
    if (phoneNumberDisabled && client_phone_number != user.phone_number) {
        return `Договор нельзя подписать так как он выставлен на номер ${client_phone_number}, а номер вашего аккаунта ${user.phone_number}`
    } else {
        return ''
    }
}

export function validateContractCreatedForAndClientRole(contract: any, form: any, user: any): string {
    if (!user || !contract) {
        return ''
    }
    // Роль из contract.system_entities. Для доп. подписанта — contractForPassportSettings(contract, index).
    let targetClientRoleEntity = entityById(contract.system_entities, clientRole.id)
    let clientRoleDisabled = !(targetClientRoleEntity?.editable ?? true)
    let targetUserRoleTitle = targetClientRoleEntity?.value == clientRoles[0] ? 'юр. лицо' : 'физ. лицо'
    let currentUserRoleTitle = user.role == clientRoles[0] ? 'юр. лица' : 'физ. лица'

    if (clientRoleDisabled && targetClientRoleEntity?.value && targetClientRoleEntity?.value != user.role) {
        return `Договор нельзя подписать так как договор должно подписать ${targetUserRoleTitle}, а у вас аккаунт ${currentUserRoleTitle}`
    } else {
        return ''
    }
}

export function validateContractCreatedForAndUserContactsBlocked(contract: any, form: any, user: any): string {
    if (!user || !contract) {
        return ''
    }
    
    let emailDisabled = !canEditSystemEntityById(contract, clientEmail.id)
    let phoneNumberDisabled = !canEditSystemEntityById(contract, clientPhoneNumber.id)
    
    if (!emailDisabled && !phoneNumberDisabled) {
        return ''
    }
    
    let formEmail = cleanEmail(form.client_email ?? "")
    let userEmail = cleanEmail(user.email ?? "")
    let client_phone_number = preparePhoneNumber(form.client_phone_number)
    let userPhoneNumber = preparePhoneNumber(user.phone_number ?? "")
    
    let emailMismatch = emailDisabled && formEmail && formEmail != userEmail
    let phoneMismatch = phoneNumberDisabled && client_phone_number && client_phone_number != userPhoneNumber
    
    if (emailMismatch || phoneMismatch) {
        return 'Вы не можете подписать этот договор, потому что инициатор указал другие контактные данные, а вы авторизованы с другими'
    }
    
    return ''
}